import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import supabase from '../lib/supabase'

const PORTAL_SESSION_KEY = 'codevaro_portal_session'

const emptyRequestForm = {
  project_id: '',
  subject: '',
  message: '',
  priority: 'normal'
}

export default function ClientPortalDashboard() {
  const navigate = useNavigate()

  const [sessionToken, setSessionToken] = useState(
    localStorage.getItem(PORTAL_SESSION_KEY) || ''
  )

  const [loading, setLoading] = useState(true)
  const [savingRequest, setSavingRequest] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const [context, setContext] = useState(null)
  const [dashboard, setDashboard] = useState(null)
  const [projects, setProjects] = useState([])
  const [requests, setRequests] = useState([])

  const [showRequestModal, setShowRequestModal] = useState(false)
  const [requestForm, setRequestForm] = useState(emptyRequestForm)

  useEffect(() => {
    if (sessionToken) {
      loadPortal()
    }
  }, [sessionToken])

  const recentRequests = useMemo(() => requests.slice(0, 5), [requests])

  async function loadPortal() {
    try {
      setLoading(true)
      setError('')
      setSuccess('')

      const [ctxRes, dashRes, projectsRes, requestsRes] = await Promise.all([
        supabase.rpc('codevaro_client_portal_session_validate', {
          p_session_token: sessionToken
        }),
        supabase.rpc('codevaro_client_portal_dashboard_by_session', {
          p_session_token: sessionToken
        }),
        supabase.rpc('codevaro_client_portal_projects_list_by_session', {
          p_session_token: sessionToken
        }),
        supabase.rpc('codevaro_client_portal_requests_list_by_session', {
          p_session_token: sessionToken
        })
      ])

      if (ctxRes.error) throw ctxRes.error
      if (dashRes.error) throw dashRes.error
      if (projectsRes.error) throw projectsRes.error
      if (requestsRes.error) throw requestsRes.error

      setContext(ctxRes.data?.[0] || null)
      setDashboard(dashRes.data?.[0] || null)
      setProjects(projectsRes.data || [])
      setRequests(requestsRes.data || [])
    } catch (err) {
      console.error(err)
      setError(err.message || 'Σφάλμα φόρτωσης portal.')
      localStorage.removeItem(PORTAL_SESSION_KEY)
      setSessionToken('')
    } finally {
      setLoading(false)
    }
  }

  function openRequestModal(projectId = '') {
    setRequestForm({
      ...emptyRequestForm,
      project_id: projectId || ''
    })
    setShowRequestModal(true)
    setError('')
    setSuccess('')
  }

  async function handleCreateRequest(e) {
    e.preventDefault()

    try {
      setSavingRequest(true)
      setError('')
      setSuccess('')

      const { error: rpcError } = await supabase.rpc(
        'codevaro_client_portal_request_create',
        {
          p_session_token: sessionToken,
          p_project_id: requestForm.project_id || null,
          p_subject: requestForm.subject,
          p_message: requestForm.message,
          p_priority: requestForm.priority || 'normal'
        }
      )

      if (rpcError) throw rpcError

      setShowRequestModal(false)
      setRequestForm(emptyRequestForm)
      setSuccess('Το αίτημά σας καταχωρήθηκε επιτυχώς.')
      await loadPortal()
    } catch (err) {
      console.error(err)
      setError(err.message || 'Σφάλμα δημιουργίας αιτήματος.')
    } finally {
      setSavingRequest(false)
    }
  }

  async function handleLogout() {
    try {
      if (sessionToken) {
        await supabase.rpc('codevaro_client_portal_logout', {
          p_session_token: sessionToken
        })
      }
    } catch (err) {
      console.error(err)
    } finally {
      localStorage.removeItem(PORTAL_SESSION_KEY)
      setSessionToken('')
      navigate('/portal', { replace: true })
    }
  }

  if (!sessionToken) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4">
        <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-lg">
          <h1 className="text-2xl font-semibold text-gray-900 mb-2">Client Portal</h1>
          <p className="text-sm text-gray-600">
            Δεν υπάρχει ενεργό session. Χρησιμοποιήστε το invite link που λάβατε.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="border-b bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Client Portal</h1>
            <p className="text-sm text-gray-600">
              {context?.client_name || '—'} · {context?.contact_name || context?.email || '—'}
            </p>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => openRequestModal()}
              className="rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
            >
              Νέο Αίτημα
            </button>

            <button
              onClick={handleLogout}
              className="rounded-lg bg-gray-100 px-4 py-2 text-gray-700 hover:bg-gray-200"
            >
              Logout
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-4 py-6">
        {loading ? (
          <div className="flex h-48 items-center justify-center">
            <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="space-y-6">
            {error ? (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700">
                {error}
              </div>
            ) : null}

            {success ? (
              <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-green-700">
                {success}
              </div>
            ) : null}

            <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
              <StatCard title="Projects" value={dashboard?.projects_count ?? 0} />
              <StatCard title="Files" value={dashboard?.files_count ?? 0} />
              <StatCard title="Updates" value={dashboard?.updates_count ?? 0} />
              <StatCard title="Requests" value={requests.length} />
            </div>

            <div className="rounded-2xl bg-white p-6 shadow">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Τα Projects σας</h2>
                  <p className="text-sm text-gray-500">
                    Παρακολούθηση έργων, αρχείων και ενημερώσεων.
                  </p>
                </div>
              </div>

              {projects.length === 0 ? (
                <p className="text-sm text-gray-500">Δεν υπάρχουν διαθέσιμα projects.</p>
              ) : (
                <div className="space-y-4">
                  {projects.map((project) => (
                    <div
                      key={project.project_id}
                      className="rounded-xl border border-gray-200 p-4 hover:border-blue-300"
                    >
                      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                        <Link
                          to={`/portal/projects/${project.project_id}`}
                          className="block flex-1"
                        >
                          <div className="font-medium text-gray-900">{project.title}</div>
                          <div className="mt-1 text-sm text-gray-600 whitespace-pre-wrap">
                            {project.portal_summary || '—'}
                          </div>
                        </Link>

                        <div className="flex flex-col gap-2 md:items-end">
                          <div className="text-sm text-gray-500">
                            <div>Status: {project.status || '—'}</div>
                            <div>Priority: {project.priority || '—'}</div>
                          </div>

                          <button
                            onClick={() => openRequestModal(project.project_id)}
                            className="rounded-lg bg-blue-50 px-3 py-2 text-sm text-blue-700 hover:bg-blue-100"
                          >
                            Αίτημα για αυτό το Project
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-2xl bg-white p-6 shadow">
              <h2 className="mb-4 text-lg font-semibold text-gray-900">Πρόσφατα Αιτήματα</h2>

              {recentRequests.length === 0 ? (
                <p className="text-sm text-gray-500">Δεν υπάρχουν αιτήματα.</p>
              ) : (
                <div className="space-y-3">
                  {recentRequests.map((request) => (
                    <RequestRow key={request.request_id} request={request} />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {showRequestModal ? (
        <RequestModal
          projects={projects}
          form={requestForm}
          setForm={setRequestForm}
          saving={savingRequest}
          onClose={() => setShowRequestModal(false)}
          onSubmit={handleCreateRequest}
        />
      ) : null}
    </div>
  )
}

function StatCard({ title, value }) {
  return (
    <div className="rounded-2xl bg-white p-6 shadow">
      <div className="text-sm text-gray-500">{title}</div>
      <div className="mt-2 text-3xl font-semibold text-gray-900">{value}</div>
    </div>
  )
}

function RequestRow({ request }) {
  return (
    <div className="rounded-lg border border-gray-200 p-4">
      <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
        <div>
          <div className="font-medium text-gray-900">{request.subject}</div>
          <div className="mt-1 text-sm text-gray-600 whitespace-pre-wrap">
            {request.message}
          </div>
          <div className="mt-2 text-xs text-gray-500">
            {request.project_title ? `Project: ${request.project_title} · ` : ''}
            {request.created_at ? new Date(request.created_at).toLocaleString('el-GR') : '—'}
          </div>
        </div>

        <div className="flex gap-2">
          <Badge value={request.status} />
          <Badge value={request.priority} />
        </div>
      </div>
    </div>
  )
}

function Badge({ value }) {
  return (
    <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700">
      {value || '—'}
    </span>
  )
}

function RequestModal({ projects, form, setForm, saving, onClose, onSubmit }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
        <div className="mb-5 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Νέο Αίτημα</h2>
            <p className="text-sm text-gray-500">
              Στείλτε μήνυμα ή αίτημα προς την ομάδα Codevaro.
            </p>
          </div>

          <button
            onClick={onClose}
            className="rounded-lg bg-gray-100 px-3 py-2 text-gray-700 hover:bg-gray-200"
          >
            ✕
          </button>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Project
            </label>
            <select
              value={form.project_id}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  project_id: e.target.value
                }))
              }
              className="w-full rounded-lg border border-gray-300 px-3 py-2"
            >
              <option value="">Γενικό αίτημα</option>
              {projects.map((project) => (
                <option key={project.project_id} value={project.project_id}>
                  {project.title}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Προτεραιότητα
            </label>
            <select
              value={form.priority}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  priority: e.target.value
                }))
              }
              className="w-full rounded-lg border border-gray-300 px-3 py-2"
            >
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Θέμα *
            </label>
            <input
              type="text"
              required
              value={form.subject}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  subject: e.target.value
                }))
              }
              className="w-full rounded-lg border border-gray-300 px-3 py-2"
              placeholder="π.χ. Αλλαγή στο website"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Μήνυμα *
            </label>
            <textarea
              required
              rows={5}
              value={form.message}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  message: e.target.value
                }))
              }
              className="w-full rounded-lg border border-gray-300 px-3 py-2"
              placeholder="Περιγράψτε τι χρειάζεστε..."
            />
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg bg-gray-100 px-4 py-2 text-gray-700 hover:bg-gray-200"
            >
              Ακύρωση
            </button>

            <button
              type="submit"
              disabled={saving}
              className="rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Αποστολή...' : 'Αποστολή Αιτήματος'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}